import { IVBattleType } from '@wg/wows-entities/types/vortex';
import equal from 'fast-deep-equal/react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { setBattleType } from '~/Actions/ActionApp';
import Select from '~/Components/Select/Select';
import { BATTLE_TYPES, DIVISIONAL_BATTLE_TYPES } from '~/constants';
import { RootState, useAppDispatch, useAppSelector } from '~/store';
import { t } from '~/utils/localization';
import { getAvailableBattleTypes, getAvailableStatBattleTypes } from '~/utils/statistics';

import DivisionPicker from '../DivisionPicker/DivisionPicker';
import styles from './BattleTypePicker.scss';

export interface BattleTypePickerProps {
  noNavigation?: boolean;
  allTypes?: boolean;
  withDivisionPicker?: boolean;
}

const stateSelector = (state: RootState) => {
  return {
    account: state.app.account,
    battleTypeRaw: state.app.battleTypeRaw,
    battleTypesMap: state.app.battleTypesMap,
  };
};

const BattleTypePicker = (props: BattleTypePickerProps) => {
  const { account, battleTypeRaw, battleTypesMap } = useAppSelector(stateSelector, equal);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<LocationParams>();

  const availableBattleTypes = getAvailableBattleTypes(account, props.allTypes);
  const battleTypesTitles = {
    pve: t('Co-op Battles'),
    pvp: t('Random Battles'),
    rank: t('Ranked Battles'),
    rank_old: t('Ranked Battles (Legacy)'),
    club: t('Team Battles'),
    clan: t('Clan Battles'),
    brawl: t('Clan Brawl'),
  };

  const filteredBattleTypes = BATTLE_TYPES.filter((battleTypeListItem) => {
    return availableBattleTypes.includes(battleTypeListItem.id);
  });

  const battleTypes = filteredBattleTypes.map((battleTypeListItem) => {
    return {
      title: battleTypesTitles[battleTypeListItem.id],
      value: battleTypeListItem.id,
    };
  });

  let battleTypeIndex = 0;
  battleTypes.forEach((battleTypeItem, index) => {
    if (battleTypeItem.value === battleTypeRaw) {
      battleTypeIndex = index;
    }
  });

  if (battleTypeIndex !== selectedIndex) {
    setSelectedIndex(battleTypeIndex);
  }

  const renderValue = () => {
    const currentBattleType: IVBattleType = battleTypesMap[filteredBattleTypes[selectedIndex].selector];
    const iconUrl = currentBattleType ? currentBattleType.icons.default : '';
    const title = battleTypes[selectedIndex] ? battleTypes[selectedIndex].title : ' ';
    return (
      <div className={styles.value}>
        <div
          className={styles.battleTypeIcon}
          style={{
            backgroundImage: `url("${iconUrl}")`,
          }}
        />
        {title}
      </div>
    );
  };

  const renderItem = (index: number) => {
    return battleTypes[index].title;
  };

  const onSelectHandler = (index: number) => {
    setSelectedIndex(index);
    const value = battleTypes[index] ? battleTypes[index].value : undefined;
    if (props.noNavigation) {
      dispatch(setBattleType(value as BattleTypeName));
    } else {
      if (value && params.id) {
        navigate(`/statistics/${params.id}/${value}`);
      }
    }
  };

  return (
    battleTypes.length !== 0 && (
      <>
        <Select
          maxItemsCount={battleTypes.length}
          items={battleTypes.map((item) => item.value)}
          selectedIndex={battleTypeIndex}
          onSelect={onSelectHandler}
          renderValue={renderValue}
          renderItem={renderItem}
        />
        {props.withDivisionPicker && battleTypes && DIVISIONAL_BATTLE_TYPES.includes(battleTypeRaw) && (
          <DivisionPicker availableStatsBattleTypes={getAvailableStatBattleTypes(account)} noNavigation={true} />
        )}
      </>
    )
  );
};

export default BattleTypePicker;
